import { useSWR, useSWRMutation } from '@tiger/swr';
import { Button } from 'antd';
import { type ComponentProps, type FC, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import BlackListWarning from 'components/BlackListWarning';
import ErrorBox from 'components/ErrorBox';
import Loading from 'components/Loading';
import Toast from 'components/Toast';
import { PlaidBankCardItem } from 'components/vendor/plaid';
import { useBlackWhiteStatus } from 'hooks/useBlackWhiteStatus';
import { ReactComponent as IconArrowRight } from 'static/images/icons/icon-arrow-right.svg';
import { ReactComponent as IconExclamationCircleOutlined } from 'static/images/icons/icon-exclamation-circle-outlined.svg';
import security from 'stores/security';
import API from 'utils/API';
import http from 'utils/http';
import { printf } from 'utils/i18n';
import { getTitleByCustomerType } from '../../helper';
import { useBankCardList } from '../../hooks/useBankCardList';
import RecurringConfig, { type RecurringConfigData } from '../components/RecurringConfig';
import styles from './style.scss';

type Props = Pick<ComponentProps<typeof RecurringConfig>, 'isIRA'>;

const Setup: FC<Props> = ({ isIRA }) => {
    const navigate = useNavigate();
    const locationState = useLocation().state as { bankInfo: PlaidBankCardItem | null; amount?: number } | null;
    const { bankInfo, amount } = locationState ?? {};

    const { data: blackListData } = useBlackWhiteStatus('ACH_DEPOSIT');

    const { data: inProgressCount } = useSWR(
        {
            url: API.TRADE.funding.getDepositArchPlansCount(),
            params: {
                bind_type: 'ACH',
                seg_type: 'SEC',
                status: 'RUNNING',
                customer_id: security.currentCustomerId,
                account_id: security.currentAccountId
            }
        },
        {
            dataChain: resp => resp.data,
            revalidateOnFocus: false
        }
    );

    const {
        data: bankCardList,
        isLoading: isBankCardListLoading,
        error: bankCardListError,
        mutate: mutateBankCardList
    } = useBankCardList();

    const { trigger: handleCreate } = useSWRMutation(
        {
            url: API.TRADE.funding.createDepositAchPlan(),
            params: {
                account_id: security.currentAccountId,
                customer_id: security.currentCustomerId
            }
        },
        (params: RecurringConfigData) => {
            return http.post(API.TRADE.funding.createDepositAchPlan(), params, {
                params: {
                    account_id: security.currentAccountId,
                    customer_id: security.currentCustomerId
                }
            });
        },
        {
            onError: err => Toast.show(err),
            onSuccess: () => {
                Toast.show(__('Submitted Successfully'));
                navigate('/profile/deposit/ach/recurring/schedules');
            }
        }
    );

    const initialValues = useMemo<Partial<RecurringConfigData>>(() => {
        // 入金页面支持选择审核中的账户，但定时入金不可选，因此检查是否在可选列表中
        const isExist = bankCardList?.some(card => card.id === bankInfo?.id);

        // 若不在可选列表中，默认使用第一个可用账户
        return {
            ach_card_id: isExist ? bankInfo?.id : bankCardList?.at(0)?.id,
            amount
        };
    }, [bankCardList, bankInfo?.id, amount]);

    const handleTipClick = () => {
        navigate('/profile/deposit/ach/recurring/schedules');
    };

    const handleDeposit = () => {
        navigate('/profile/deposit/ach/fill', {
            state: bankInfo,
            replace: true
        });
    };

    if (isBankCardListLoading) {
        return <Loading />;
    }

    if (bankCardListError && !bankCardList) {
        return (
            <ErrorBox
                error={bankCardListError}
                onClick={() => {
                    mutateBankCardList();
                }}
            />
        );
    }

    if (blackListData?.isBlack) {
        return <BlackListWarning source="deposit" type="ACH" />;
    }

    return (
        <div className={styles.pageRoot}>
            {Boolean(inProgressCount) ? (
                <div className={styles.topAlert} onClick={handleTipClick}>
                    <IconExclamationCircleOutlined className={styles.icon} />
                    {printf(
                        isIRA
                            ? __('%s recurring contribution tasks are currently in progress.')
                            : __('%s recurring deposit tasks are currently in progress.'),
                        inProgressCount
                    )}
                </div>
            ) : null}
            <RecurringConfig
                bankCardList={bankCardList}
                initialValues={initialValues}
                isIRA={isIRA}
                subTitle={
                    <Button type="link" className={styles.depositLink} onClick={handleDeposit}>
                        {getTitleByCustomerType()}
                        <IconArrowRight fontSize={14} />
                    </Button>
                }
                onSubmit={handleCreate}
            />
        </div>
    );
};

export default Setup;
